import { Component, EventEmitter, OnInit, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ApiService } from '../api.service';
import { CacheService } from '../cache.service';
import { CourseService } from '../course.service';
import { CourseSessionsService } from '../course-sessions.service';
import { CourseSession } from '../course-session.model';
import { SessionMap } from '../session-map.model';

@Component({
    selector: 'app-course-session',
    templateUrl: './course-session.component.html',
    styleUrls: ['./course-session.component.scss'],
})
export class CourseSessionComponent implements OnInit {
    @Input() session: CourseSession;
    // @Output() cancelAdd = new EventEmitter<boolean>();

    form;
    isAdd: boolean;
    isLoading: boolean = false;
    isLoaded: boolean = false;
    isSubmitted: boolean = false;
    isSaving: boolean = false;
    isValid: boolean = false;
    isEditable: boolean = false;
    loadingError: string = '';
    errorMessage: string = '';
    display: boolean = false;
    // display: boolean = true;

    a2eOptions = {
        format: 'MM/DD/YYYY hh:mm:ss A',
        showClear: true,
        showClose: true,
    };

    constructor(
        private apiService: ApiService,
        private cacheService: CacheService,
        private courseService: CourseService,
        private courseSessionsService: CourseSessionsService,
        private fb: FormBuilder
    ) {}

    ngOnInit() {
        this.isAdd = !this.session;
        if (this.isAdd) {
            this.session = new CourseSession({});
            this.isEditable = true;
            this.isLoaded = true;
            this.display = true;
        }
        this.prepareForm();
    }

    prepareForm() {
        this.form = this.fb.group({
            id: [this.session.id, []],
            name: [
                this.session.name,
                [Validators.required, Validators.maxLength(300)],
            ],
            description: [
                this.session.description,
                [Validators.maxLength(4000)],
            ],
            startDate: [this.session.startDate, [Validators.required]],
            endDate: [this.session.endDate, []],
            checkInDate: [this.session.checkInDate, []],
            minAttendance: [this.session.minAttendance, []],
            maxAttendance: [this.session.maxAttendance, []],
            sortOrder: [this.session.sortOrder, []],
            required: [this.session.required, []],
            status: [this.session.status.id, [Validators.required]],
            deliveryMethod: [this.session.deliveryMethod.id, []],
            topicKeywords: [
                this.session.topicKeywords,
                [Validators.maxLength(120)],
            ],
            privateUrl: [this.session.privateUrl, [Validators.maxLength(200)]],
            eedsAin: [this.session.eedsAin, [Validators.maxLength(9)]],
            landingPage: [this.session.landingPage, []],
            joinUrl: [this.session.joinUrl, [Validators.maxLength(400)]],
            docebo: [("docebo" in this.session.integrations), []],
            doceboShowOpen: [("docebo" in this.session.integrations && this.session.integrations.docebo.showOpen), []],
            zoom: [("zoom" in this.session.integrations), []],
            zoomShowOpen: [("zoom" in this.session.integrations && this.session.integrations.zoom.showOpen), []],
        });

        this.form.valueChanges.subscribe((value) => {
            this.isValid = this.form.dirty && !this.form.invalid;
        });
    }

    get() {
        if (this.isLoading) return;

        this.isEditable = false;
        this.isSaving = false;
        this.isLoading = true;
        this.loadingError = '';

        this.apiService.getCourseSession(this.course, this.session).subscribe(
            (data) => {
                this.session = new CourseSession(data);
                this.isLoaded = true;
                this.isLoading = false;
            },
            (error) => {
                this.isLoaded = true;
                this.isLoading = false;
                this.loadingError = error;
            }
        );
    }

    collapse() {
        if (this.isEditable) return;
        if (!this.isLoaded) this.get();
        this.display = !this.display;
    }

    edit() {
        this.prepareForm();
        this.isEditable = true;
        this.courseSessionsService.isEditable = true;
    }

    cancel() {
        this.errorMessage = '';
        this.isEditable = false;
        this.courseSessionsService.isEditable = false;
        this.courseSessionsService.isAdd = false;
        this.form.reset(this.session);
    }

    save() {
        this.isSubmitted = true;
        if (!this.isValid || this.isSaving) return;
        this.isSaving = true;
        this.errorMessage = ``;

        if (!this.isAdd) {
            this.apiService
                .updateCourseSession(this.course, this.form.value)
                .subscribe(
                    (data) => {
                        this.courseSessionsService.isAdd = false;
                        this.courseSessionsService.isEditable = false;
                        this.courseSessionsService.get();
                    },
                    (error) => {
                        this.errorMessage = error;
                        this.isSaving = false;
                    }
                );
            return;
        }

        this.apiService
            .createCourseSession(this.course, this.form.value)
            .subscribe(
                (data) => {
                    this.courseSessionsService.isAdd = false;
                    this.courseSessionsService.isEditable = false;
                    this.courseSessionsService.get();
                },
                (error) => {
                    this.errorMessage = error;
                    this.isSaving = false;
                }
            );
    }
    
    open(url){
        window.open(url, "_blank");
    }

    openDocebo(){
        window.open(`/dashboard/lms/${this.session.integrations.docebo.applicationId}`, "_blank");
    }

    openZoom(){
        console.log(`open zoom`)
        // window.open(`/dashboard/lms/${this.session.integrations.zoom.applicationId}`, "_blank");
    }

    get deliveryMethods() {
        return this.cacheService.deliveryMethods;
    }

    get statuses() {
        return this.cacheService.statuses;
    }

    get yesNo() {
        return this.cacheService.yesNo;
    }

    get course() {
        return this.courseService.course;
    }

    get name() {
        return this.form.get('name');
    }

    get description() {
        return this.form.get('description');
    }

    get startDate() {
        return this.form.get('startDate');
    }

    get endDate() {
        return this.form.get('endDate');
    }

    get checkInDate() {
        return this.form.get('checkInDate');
    }

    get minAttendance() {
        return this.form.get('minAttendance');
    }

    get maxAttendance() {
        return this.form.get('maxAttendance');
    }

    get sortOrder() {
        return this.form.get('sortOrder');
    }

    get required() {
        return this.form.get('required');
    }

    get status() {
        return this.form.get('status');
    }

    get deliveryMethod() {
        return this.form.get('deliveryMethod');
    }

    get topicKeywords() {
        return this.form.get('topicKeywords');
    }

    get privateUrl() {
        return this.form.get('privateUrl');
    }

    get eedsAin() {
        return this.form.get('eedsAin');
    }

    get landingPage() {
        return this.form.get('landingPage');
    }

    get joinUrl() {
        return this.form.get('joinUrl');
    }
    
    get docebo() {
        return this.form.get('docebo');
    }

    get doceboShowOpen() {
        return this.form.get('doceboShowOpen');
    }

    get zoom() {
        return this.form.get('zoom');
    }
    
    get zoomShowOpen() {
        return this.form.get('zoomShowOpen');
    }
}
